import { authClient } from '@/utils/api'
import { AuthResponse, OidcClientConfig, RefreshTokenInput } from '../types/authTypes'
import { MutationConfig } from '@/utils/reactQuery'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { getConfigOptions } from './getConfig'

type RefreshTokenMutationFunctionProps = {
  refreshToken: string
}

type RefreshTokenProps = RefreshTokenMutationFunctionProps & {
  config: OidcClientConfig
}

const refreshToken = async ({ refreshToken, config }: RefreshTokenProps): Promise<AuthResponse> => {
  const body: RefreshTokenInput = {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
  }
  const { data } = await authClient.post(new URL(config.token_endpoint).toString(), new URLSearchParams(body))
  return data
}

type UseRefreshTokenProps = {
  mutationConfig?: MutationConfig<MutationFunction<AuthResponse, RefreshTokenMutationFunctionProps>>
}

const useRefreshToken = ({ mutationConfig }: UseRefreshTokenProps = {}) => {
  const { ...restConfig } = mutationConfig || {}
  const queryClient = useQueryClient()

  return useMutation({
    ...restConfig,
    mutationFn: async ({ refreshToken: token }: RefreshTokenMutationFunctionProps) => {
      const configQuery = getConfigOptions()
      const config = queryClient.getQueryData(configQuery.queryKey) ?? (await queryClient.fetchQuery(configQuery))
      return refreshToken({ refreshToken: token, config })
    },
  })
}

export { refreshToken, useRefreshToken }
